import { useState, useEffect } from 'react'

interface PWAState {
    isStandalone: boolean
    isInstallable: boolean
    isMobile: boolean
    shouldShowInstallPrompt: boolean
}

export function usePWA(): PWAState {
    const [state, setState] = useState<PWAState>({
        isStandalone: false,
        isInstallable: false,
        isMobile: false,
        shouldShowInstallPrompt: false
    })

    useEffect(() => {
        const checkPWAStatus = () => {
            // Check if running in standalone mode (PWA)
            const isStandalone =
                window.matchMedia('(display-mode: standalone)').matches ||
                (window.navigator as { standalone?: boolean }).standalone === true ||
                document.referrer.includes('android-app://') ||
                window.location.search.includes('utm_source=homescreen')

            // Check if on mobile device
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                window.innerWidth <= 768

            // Check if app is installable (has beforeinstallprompt event support)
            const isInstallable = 'onbeforeinstallprompt' in window

            // Show install prompt if on mobile and not in standalone mode
            const shouldShowInstallPrompt = isMobile && !isStandalone

            setState({
                isStandalone,
                isInstallable,
                isMobile,
                shouldShowInstallPrompt
            })
        }

        // Initial check
        checkPWAStatus()

        // Listen for display mode changes
        const mediaQuery = window.matchMedia('(display-mode: standalone)')
        const handleDisplayModeChange = () => checkPWAStatus()

        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleDisplayModeChange)
        } else {
            // Fallback for older browsers
            mediaQuery.addListener(handleDisplayModeChange)
        }

        // Listen for window resize to update mobile detection
        window.addEventListener('resize', checkPWAStatus)

        return () => {
            if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener('change', handleDisplayModeChange)
            } else {
                mediaQuery.removeListener(handleDisplayModeChange)
            }
            window.removeEventListener('resize', checkPWAStatus)
        }
    }, [])

    return state
} 